import { useRef } from "react";

import { Dayjs } from "dayjs";
import { IoMdCalendar } from "react-icons/io";
import { useShallow } from "zustand/react/shallow";

import DatePicker from "@/components/DatePicker";
import useModal from "@/hooks/useModal";
import { useOutSideClick } from "@/hooks/useOutSideClick";
import useCalendarStore from "@/store/calendarStore";
import { cn } from "@/utils/cn";

interface DropdownDatePickerProps {
  startDate?: Dayjs;
  date?: Dayjs;
  limit?: number;
  onChange: (day: Dayjs) => void;
}

const DropdownDatePicker = ({
  startDate,
  date,
  onChange,
}: DropdownDatePickerProps) => {
  const [setFirstDayOfMonth] = useCalendarStore(
    useShallow(state => [state.setFirstDayOfMonth]),
  );

  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsModal = useModal();
  useOutSideClick(dropdownRef, () => optionsModal.onClose());

  return (
    <div ref={dropdownRef} className="relative h-10 flex-1">
      {/* Trigger */}
      <button
        type="button"
        className={cn(
          "absolute top-0 z-40 flex h-10 w-full select-none items-center justify-between rounded-lg border bg-white px-3 text-sm transition-colors",
          optionsModal.isOpen
            ? "border-primary ring-1 ring-primary/20"
            : "border-gray-300 hover:border-primary/50",
        )}
        onClick={() => {
          if (date) setFirstDayOfMonth(date?.startOf("month"));
          optionsModal.onToggle();
        }}
      >
        <span className="text-gray-dark">
          {date ? date?.format("YYYY / MM / DD") : "---- / -- / --"}
        </span>
        <IoMdCalendar size={14} className="text-gray-400" />
      </button>

      {/* Calendar Popup */}
      {optionsModal.isOpen && (
        <div className="absolute left-0 top-12 z-50 w-[400px] rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
          <DatePicker startDate={startDate} date={date} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

export default DropdownDatePicker;
